import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Collapse,
  Tooltip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Recursive tree node renderer for the Knowledge Base directory pane.
 *
 * Props:
 *   node        — { name, path, type: 'folder'|'file', children: [] }
 *   depth       — nesting level (0 = root)
 *   expanded    — Set<string> of currently-expanded folder paths
 *   onToggle    — (path) => void — toggle a folder open/closed
 *   currentPath — the currently displayed document path (highlight active file)
 *   onFileSelect — (path) => void — called when a file node is clicked
 *   canEdit     — boolean — show mutation controls only when true
 *   onCreateFile   — (parentFolderPath) => void
 *   onCreateFolder — (parentFolderPath) => void
 *   onDelete       — (node) => void
 */
export default React.memo(function DirectoryTreeNode({
  node,
  depth,
  expanded,
  onToggle,
  currentPath,
  onFileSelect,
  canEdit,
  onCreateFile,
  onCreateFolder,
  onDelete,
}) {
  const indent = depth * 16;
  const isFolder = node.type === 'folder';
  const isOpen = expanded.has(node.path);
  const isActive = !isFolder && node.path === currentPath;
  const isReadOnly = node.path === 'youtube-videos' || node.path.startsWith('youtube-videos/');

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    pl: `${indent + 4}px`,
    pr: 0.5,
    py: 0.25,
    cursor: isFolder ? 'default' : 'pointer',
    borderRadius: 1,
    bgcolor: isActive ? 'action.selected' : 'transparent',
    '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
    '&:hover .kb-row-actions': { opacity: 1 },
  };

  return (
    <>
      <Box sx={rowSx} onClick={!isFolder ? () => onFileSelect(node.path) : undefined}>
        {/* Expand / collapse toggle for folders */}
        {isFolder ? (
          <IconButton
            size="small"
            sx={{ p: 0.25, mr: 0.25 }}
            onClick={(e) => { e.stopPropagation(); onToggle(node.path); }}
            aria-label={isOpen ? 'Collapse folder' : 'Expand folder'}
          >
            {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          /* Spacer to align file nodes with folder text */
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}

        {/* Icon */}
        {isFolder ? (
          isOpen
            ? <FolderOpenIcon fontSize="small" color="primary" sx={{ mr: 0.75, flexShrink: 0 }} />
            : <FolderIcon fontSize="small" color="primary" sx={{ mr: 0.75, flexShrink: 0 }} />
        ) : (
          <InsertDriveFileIcon fontSize="small" color="action" sx={{ mr: 0.75, flexShrink: 0 }} />
        )}

        {/* Name */}
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            fontSize: '0.8125rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'primary.main' : 'text.primary',
            minWidth: 0,
          }}
          title={node.name}
        >
          {node.name}
        </Typography>

        {/* Admin action buttons — hidden until row is hovered */}
        {canEdit && !isReadOnly && (
          <Box
            className="kb-row-actions"
            sx={{ display: 'flex', alignItems: 'center', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isFolder && (
              <>
                <Tooltip title="New file" placement="top">
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onCreateFile(node.path)}>
                    <NoteAddIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="New folder" placement="top">
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onCreateFolder(node.path)}>
                    <CreateNewFolderIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Delete" placement="top">
              <IconButton size="small" sx={{ p: 0.25, color: 'error.main' }} onClick={() => onDelete(node)}>
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Children (folders only) */}
      {isFolder && (
        <Collapse in={isOpen} unmountOnExit>
          {node.children.map((child) => (
            <DirectoryTreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              currentPath={currentPath}
              onFileSelect={onFileSelect}
              canEdit={canEdit}
              onCreateFile={onCreateFile}
              onCreateFolder={onCreateFolder}
              onDelete={onDelete}
            />
          ))}
          {node.children.length === 0 && (
            <Typography
              variant="caption"
              sx={{ pl: `${indent + 52}px`, color: 'text.disabled', display: 'block', py: 0.25, fontSize: '0.75rem' }}
            >
              empty
            </Typography>
          )}
        </Collapse>
      )}
    </>
  );
})
